import SettingsIcon from '@mui/icons-material/Settings';
import { Checkbox, ListItemText, Menu, MenuItem } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { ImageBox } from '../../../components/BoxedImage';
import { PlanogramComplianceResponse } from '../../../types';
import {
  buildClipStripBadgeBoxes,
  buildOversizedPtBadgeBoxes,
} from '../../../utils/clipStripBadges';

const SHOW_CLIP_STRIP_BADGES_KEY = 'pc:showClipStripBadges';
const SHOW_OVERSIZED_PT_BADGES_KEY = 'pc:showOversizedPtBadges';

// Persisted badge toggles default to on; only an explicit "false" hides them.
const readBadgePref = (key: string): boolean => localStorage.getItem(key) !== 'false';

export interface ShelfBadges {
  /** The non-interactive badge overlay boxes, gated by the toggles. Pass these
   * to the viewer's `overlayBadges` prop (they are decorations, not actions). */
  badgeBoxes: ImageBox[];
  /** True when the current image has at least one badge to place (ignoring toggles). */
  hasAnyBadges: boolean;
  /** The gear button + settings menu; render in the "Camera Images" title area. */
  settingsMenu: React.ReactNode;
}

/**
 * Per-shelf clip-strip / oversized-PT decision badges plus their visibility
 * settings, shared by every view that renders planogram-compliance overlays.
 *
 * The caller passes `badgeBoxes` to `CameraImageViewer`'s `overlayBadges` prop
 * and renders `settingsMenu` in the title area (only when `hasAnyBadges`).
 */
export function useShelfBadges(
  planogramComplianceData: PlanogramComplianceResponse | null | undefined
): ShelfBadges {
  const [showClipStripBadges, setShowClipStripBadges] = useState(() =>
    readBadgePref(SHOW_CLIP_STRIP_BADGES_KEY)
  );
  const [showOversizedPtBadges, setShowOversizedPtBadges] = useState(() =>
    readBadgePref(SHOW_OVERSIZED_PT_BADGES_KEY)
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const toggleClipStripBadges = useCallback(() => {
    setShowClipStripBadges((prev) => {
      const next = !prev;
      localStorage.setItem(SHOW_CLIP_STRIP_BADGES_KEY, String(next));
      return next;
    });
  }, []);

  const toggleOversizedPtBadges = useCallback(() => {
    setShowOversizedPtBadges((prev) => {
      const next = !prev;
      localStorage.setItem(SHOW_OVERSIZED_PT_BADGES_KEY, String(next));
      return next;
    });
  }, []);

  const clipStripBadgeBoxes = useMemo(
    () =>
      buildClipStripBadgeBoxes(
        planogramComplianceData?.boxes,
        planogramComplianceData?.clipStripDecisions
      ),
    [planogramComplianceData]
  );

  const oversizedPtBadgeBoxes = useMemo(
    () =>
      buildOversizedPtBadgeBoxes(
        planogramComplianceData?.boxes,
        planogramComplianceData?.oversizedPtDecisions
      ),
    [planogramComplianceData]
  );

  const availableBadgeKinds = {
    clipStrip: clipStripBadgeBoxes.length > 0,
    oversizedPt: oversizedPtBadgeBoxes.length > 0,
  };
  const hasAnyBadges = availableBadgeKinds.clipStrip || availableBadgeKinds.oversizedPt;

  const badgeBoxes = useMemo(
    () => [
      ...(showClipStripBadges ? clipStripBadgeBoxes : []),
      ...(showOversizedPtBadges ? oversizedPtBadgeBoxes : []),
    ],
    [showClipStripBadges, showOversizedPtBadges, clipStripBadgeBoxes, oversizedPtBadgeBoxes]
  );

  const settingsMenu = hasAnyBadges ? (
    <>
      <button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className={`p-2 rounded-md transition-colors ${
          anchorEl
            ? 'bg-blue-100 text-blue-700 border border-blue-300'
            : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
        }`}
        title="Badge display settings"
      >
        <SettingsIcon className="w-5 h-5" fontSize="small" />
      </button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {availableBadgeKinds.clipStrip && (
          <MenuItem onClick={toggleClipStripBadges} dense>
            <Checkbox edge="start" size="small" checked={showClipStripBadges} disableRipple />
            <ListItemText primary="Clip-strip badges" />
          </MenuItem>
        )}
        {availableBadgeKinds.oversizedPt && (
          <MenuItem onClick={toggleOversizedPtBadges} dense>
            <Checkbox edge="start" size="small" checked={showOversizedPtBadges} disableRipple />
            <ListItemText primary="Oversized PT badges" />
          </MenuItem>
        )}
      </Menu>
    </>
  ) : null;

  return { badgeBoxes, hasAnyBadges, settingsMenu };
}
